import { createEntityAdapter, EntityState } from "@ngrx/entity";
import { createReducer, on } from "@ngrx/store";
import { AdditionalCostsDto, IMaxxOptimizerQueryDto } from "@core/models/http/treatment/treatment-plans/optimizer-dto";
import {
  ImaxxActions,
  TreatmentPageActions,
  TreatmentPlanApiActions,
} from "../actions/action-index";
import { mapTreatmentPlanOptimization } from "./maps";

export const featureKey = "imaxx";

export type ImaxxState = EntityState<IMaxxOptimizerQueryDto>;

export const adapter = createEntityAdapter<IMaxxOptimizerQueryDto>({
  selectId: (imaxxOptimized: IMaxxOptimizerQueryDto) =>
    imaxxOptimized.TreatmentPlanId,
});

export const initialState: ImaxxState = adapter.getInitialState();

export const reducer = createReducer(
  initialState,
  on(
    TreatmentPlanApiActions.createTreatmentPlanOptimizerSuccess,
    (state, action) => {
      if (action.imaxxOptimized) {
        return adapter.setOne({ ...action.imaxxOptimized }, state);
      } else {
        return state;
      }
    }
  ),
  on(TreatmentPlanApiActions.loadSuccess, (state, { contract }) => {
    return adapter.setAll(
      contract.TreatmentPlans.map((tp) => mapTreatmentPlanOptimization(tp)),
      {
        ...state,
      }
    );
  }),
  on(ImaxxActions.updateImaxxOptimizerSuccess, (state, action) => {
    return adapter.setOne({ ...action }, state);
  }),
  on(ImaxxActions.optimizedCalculateSuccess, (state, action) => {
    let updatedAdditionalCosts = action.TreatmentPlan?.TreatmentOptimizer?.AdditionalCosts || [] as AdditionalCostsDto[];

    // If selections have not been submitted and the network discount changes to $0, then unselect any selected includable costs
    // This can happen when treatment selections change
    if (action.ImaxxEvaluationResult.IMaxxStatus.OptimizerStep === 1 && action.TreatmentPlanEstimate.NetworkDiscount === 0) {
      updatedAdditionalCosts = updatedAdditionalCosts?.map(cost => 
        cost.IsIncludable ? { ...cost, SelectedOn: null } : cost
      );
    }

    return adapter.updateOne(
      {
        id: action.TreatmentPlan.TreatmentPlanId,
        changes: {
          ImaxxEvaluationResult: action.ImaxxEvaluationResult,
          TreatmentPlan: action.TreatmentPlan,
          TreatmentPlanBenefit: action.TreatmentPlanEstimate,
          AdditionalCosts: updatedAdditionalCosts
        },
      },
      state
    );
  }),
  on(ImaxxActions.toggleSelectImaxxAdditionalCost, (state, action) => {
    return adapter.updateOne(
      {
        id: action.treatmentPlanId,
        changes: {
          AdditionalCosts: state.entities[action.treatmentPlanId].AdditionalCosts.map(
            (cost) => cost.Id === action.additionalCostId
            ? { ...cost, SelectedOn: action.selectedOn }
            : cost
          ),
        },
      },
      state
    );
  }),
  on(TreatmentPageActions.additionalCostCompleted, (state, action) => {
    return adapter.updateOne(
      {
        id: action.treatmentPlanId,
        changes: {
          ImaxxEvaluationResult: {
            ...state.entities[action.treatmentPlanId].ImaxxEvaluationResult,
            IMaxxStatus: {
              ...state.entities[action.treatmentPlanId].ImaxxEvaluationResult
                .IMaxxStatus,
              OptimizerStep:
                action.selectedAdditionalCosts.length > 0 ||
                action.selectedIncludableCosts.length > 0
                  ? 3
                  : state.entities[action.treatmentPlanId].ImaxxEvaluationResult
                      .IMaxxStatus.OptimizerStep,
            },
          },
        },
      },
      state
    );
  })
);
